import { useMemo } from "react";
import zohoCRM from "V2.0/images/zohoCRM.svg";
import GHL from "V2.0/images/GHL.png";
import Twilio from "V2.0/images/twilio.svg";
import ZapierImage from "V2.0/images/zapier.svg";
import whatsapp from "V2.0/images/whatsapp.svg";
import messenger from "V2.0/images/messenger.svg";
import S3 from "V2.0/images/s3.svg";
import Chroma from "V2.0/images/chroma.svg";
import pinecone from "V2.0/images/pinecone.png";
import Plivo from "V2.0/images/plivo.svg";
import instagram from "V2.0/images/instagram.png";
import Acuity from "V2.0/images/acuity.png";
import { useNavigate, useOutletContext, useLocation } from "react-router-dom";
import Make from "V2.0/images/make.svg";
import mcube from "V2.0/images/mcube.png";
import cloudonix from "V2.0/images/cloudonix.png";
import customSip from "V2.0/images/customSip.svg";
import ExotelIcon from "V2.0/HomePages/Integrations/Telephony/Exotel/ExotelLogo";
import TataSmartflow from "V2.0/images/tata-smart-flo.png";

const ImageIcon = ({ src, alt, width }) => (
  <img
    src={src}
    alt={alt}
    style={{ width: `${width}px` }}
  />
);

function MenuItem({ name, Icon, iconProps, onClick, description }) {
  return (
    <div
      className={`flex flex-col gap-y-4 h-32 w-full md:w-70 border border-gray-300 p-4 pb-8 rounded-lg cursor-pointer hover:bg-gray-100`}
      onClick={onClick}
    >
      <div className="flex h-10 items-center gap-x-2">
        {Icon ? <Icon /> : <ImageIcon {...iconProps} />}
        <span className="font-medium text-sm">{name}</span>
      </div>
      {description && (
        <div>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
      )}
    </div>
  );
}

function SectionComponent(props) {
  const { section } = props;
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div
      className="mt-8"
      key={section.title}
    >
      <div className="flex justify-start">
        <span className="pr-2 text-md font-medium text-gray-800 mb-3">
          {section.title}
        </span>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {section.items.map((item) => (
          <MenuItem
            key={item.name}
            name={item.name}
            Icon={item.Icon}
            iconProps={item.iconProps}
            onClick={() =>
              navigate({
                pathname: item.href,
                search: location.search,
              })
            }
            description={item.description}
          />
        ))}
      </div>
    </div>
  );
}

const communicationIntegrations = {
  title: "Communication",
  items: [
    {
      id: "Whatsapp",
      name: "Whatsapp Business",
      iconProps: { src: whatsapp, width: 20 },
      href: "/integrations/whatsapp",
      description: "Manage your Whatsapp Business conversations",
    },
    {
      id: "Messenger",
      name: "Messenger",
      iconProps: { src: messenger, width: 20 },
      href: "/integrations/messenger",
      description: "Manage your Facebook Page conversations",
    },
    {
      id: "Instagram",
      name: "Instagram",
      iconProps: { src: instagram, width: 20 },
      href: "/integrations/instagram",
      description: "Manage your Instagram Page conversations",
    },
  ],
};

const databaseIntegrations = {
  title: "Database",
  items: [
    {
      id: "S3",
      name: "S3",
      href: "/integrations/s3",
      iconProps: { src: S3, width: 30 },
      description: "Connect to store your data in your S3 storage",
    },
    {
      id: "Pinecone",
      name: "Pinecone",
      href: "/integrations/pinecone",
      iconProps: { src: pinecone, alt: "Pinecone", width: 20 },
      description: "Connect to your Pinecone DB",
    },
    {
      name: "",
      id: "ChromaDB",
      href: "/integrations/chromadb",
      iconProps: { src: Chroma, alt: "ChromaDB", width: 80 },
      description: "Connect to your Chroma DB",
    },
  ],
};

const automationIntegrations = {
  title: "Automation",
  items: [
    {
      id: "Zapier",
      name: "Zapier",
      iconProps: { src: ZapierImage, width: 30 },
      href: "/integrations/zapier",
      description: "Automate your workflows with zapier",
    },
    {
      id: "Make",
      name: "",
      iconProps: { src: Make, width: 80 },
      href: "/integrations/make",
      description: "Automate your workflows with make",
    },
  ],
};

const telephonyIntegrations = {
  title: "Telephony",
  items: [
    {
      id: "Twilio",
      name: "Twilio",
      iconProps: { src: Twilio, width: 30 },
      href: "/integrations/twilio",
      description: "Access Twilio phone numbers for voice calls",
    },
    {
      id: "Plivo",
      name: "Plivo",
      iconProps: { src: Plivo, width: 35 },
      href: "/integrations/plivo",
      description: "Access Plivo phone numbers for voice calls",
    },
  ],
};

const telephonyIntegrationMCube = {
  id: "MCube",
  name: "MCube",
  iconProps: { src: mcube, width: 30 },
  href: "/integrations/mcube",
  description: "Access MCube phone numbers for voice calls",
};

const telephonyIntegrationCloudonix = {
  id: "Cloudonix",
  name: "Cloudonix",
  iconProps: { src: cloudonix, width: 30 },
  href: "/integrations/cloudonix",
  description: "Access Cloudonix phone numbers for voice calls",
};

const telphonyIntegrationCustomSIP = {
  id: "Custom SIP",
  name: "Custom SIP",
  iconProps: { src: customSip, width: 40 },
  href: "/integrations/custom-sip",
  description: "Access Custom SIP phone numbers for voice calls",
};

const telephonyIntegrationExotel = {
  id: "Exotel",
  name: "Exotel",
  Icon: () => <ExotelIcon />,
  href: "/integrations/exotel",
  description: "Access Exotel Phone Numbers",
};

const telephonyIntegrationTataSmartflo = {
  id: "Smartflo",
  name: "Tata Smartflo",
  iconProps: { src: TataSmartflow, width: 50 },
  href: "/integrations/tata-smartflo",
  description: "Access Tata Smarflo phone numbers",
};

const contactsAndCalendarIntegrations = {
  title: "Contacts & Calendars",
  items: [
    {
      id: "ZohoCRM",
      name: "Zoho CRM",
      iconProps: { src: zohoCRM, width: 40 },
      href: "/integrations/zoho-crm",
      description: "Search and access contacts in Zoho CRM ",
    },
    {
      id: "GHL",
      name: "GoHighLevel",
      iconProps: { src: GHL, width: 30 },
      href: "/integrations/ghl",
      description: "Search contacts and book meetings in GHL",
    },
    {
      id: "Acuity",
      name: "Acuity",
      iconProps: { src: Acuity, width: 30 },
      href: "/integrations/acuity",
      description: "Book appointments in Acuity",
    },
    {
      id: "Cal",
      iconProps: { src: "https://cal.com/logo.svg", width: 60 },
      href: "/integrations/cal",
      description: "Book appointments in Cal.com",
    },
  ],
};

function getTelephonyIntegrations(integrationFeatureFlags) {
  const { title, items } = telephonyIntegrations;

  const updatedTelephonyIntegrations = {
    title,
    items: [
      ...items,
      ...(integrationFeatureFlags?.showMCubeIntegration
        ? [telephonyIntegrationMCube]
        : []),
      ...(integrationFeatureFlags?.showCloudonixIntegration
        ? [telephonyIntegrationCloudonix]
        : []),
      ...(integrationFeatureFlags?.showCustomSipIntegration
        ? [telphonyIntegrationCustomSIP]
        : []),
      telephonyIntegrationTataSmartflo,
      ...(integrationFeatureFlags?.showExotelIntegration
        ? [telephonyIntegrationExotel]
        : []),
    ],
  };

  return updatedTelephonyIntegrations;
}

export function Integrations() {
  const { company } = useOutletContext();

  const integrationSections = useMemo(() => {
    if (company && company?.thirdPartyIntegrationVisibility) {
      const updatedTelephonyIntegrations = getTelephonyIntegrations(
        company?.thirdPartyIntegrationVisibility,
      );

      return [
        updatedTelephonyIntegrations,
        communicationIntegrations,
        contactsAndCalendarIntegrations,
        automationIntegrations,
        databaseIntegrations,
      ];
    }

    return [
      telephonyIntegrations,
      communicationIntegrations,
      contactsAndCalendarIntegrations,
      automationIntegrations,
      databaseIntegrations,
    ];
  }, [company]);

  return (
    <div className="w-full h-full">
      <main className="max-w-6xl mx-auto h-full flex flex-col gap-y-6">
        <div className="pt-8 px-8">
          <p className="font-bold text-2xl">Integrations</p>
        </div>
        <div className="h-[calc(100vh-100px)] overflow-auto">
          <div className="flex flex-col px-8 mb-8">
            {integrationSections.map((integration) => (
              <SectionComponent section={integration} />
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
